import React from "react";
import { useSEO } from "@/hooks/useSEO";
import { Section, SectionHeading, Reveal, CTASection, Marquee, Eyebrow, Button } from "@/components/ui-bits";
import { PageHero } from "@/components/shared";
import { WHY, AUDIENCE } from "@/lib/data";

export default function About() {
  useSEO("About Wisperwick Studio | Beverage Mix Manufacturing Partner",
    "Wisperwick Studio is the manufacturing bridge between an idea and a finished product — helping young, growing and established brands go from idea to shelf.");
  return (
    <div>
      <PageHero eyebrow="About Wisperwick Studio" title="You have the idea. We help build the product."
        sub="Wisperwick Studio provides the manufacturing bridge between an idea and a finished product." />

      <Section className="pt-0">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading title="Great product ideas shouldn't get stuck in a notebook." />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-lg text-ws-text2 leading-relaxed max-w-2xl">
                <p>Many young brands have the idea, the audience and the ambition — but not the manufacturing infrastructure to bring the product to life. Wisperwick Studio exists to bridge that gap.</p>
                <p>We bring development and manufacturing capabilities together under one partner, so founders can move from <span className="text-ws-dark italic font-display">"I have an idea"</span> to <span className="text-ws-dark italic font-display">"I have something ready to launch."</span></p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal delay={0.15}>
              <div className="bg-ws-dark rounded-2xl p-6 sm:p-10">
                <p className="font-display text-3xl text-ws-bg leading-tight">You build the brand.<br/><span className="text-ws-accent italic">We build the product.</span></p>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Marquee dark items={["From concept to product", "Your formula. Our manufacturing", "Your idea. Our process", "Ready to bring it to life?", "From idea to shelf"]} />

      <Section>
        <SectionHeading eyebrow="Why brands choose us" title="More than a factory." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={(i % 3) * 0.07}>
              <div className="border-t border-ws-border pt-6">
                <span className="font-display text-ws-accent text-xl">0{i + 1}</span>
                <h3 className="font-display text-2xl text-ws-dark mt-3 leading-tight">{w.title}</h3>
                <p className="text-ws-text2 text-sm mt-3 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section bg="var(--ws-bg2)">
        <SectionHeading eyebrow="Contact" title="Let's build something worth putting on the shelf."
          sub="Whether you have a finished formulation or only the first idea, start the conversation with Wisperwick Studio." />
        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4 mt-14">
          {[["Email", "wisperwickstudio@gmail.com", "mailto:wisperwickstudio@gmail.com"], ["Phone", "+91 99109 12419", "tel:+919910912419"], ["Business Hours", "Mon–Saturday, 9AM–7PM", null]].map(([k, v, href], i) => (
            <Reveal key={k} delay={i * 0.06}>
              <div className="bg-ws-card border border-ws-border rounded-xl p-6 h-full">
                <p className="label text-ws-accent">{k}</p>
                {href
                  ? <a href={href} data-testid={`about-${k.toLowerCase()}`} className="text-ws-dark mt-3 min-h-11 flex items-center hover:text-ws-accent transition-colors break-all">{v}</a>
                  : <p data-testid="about-business-hours" className="text-ws-dark mt-3">{v}</p>}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}><div className="mt-12"><Button to="/request-a-quote" testid="about-cta">Request a Quote</Button></div></Reveal>
      </Section>

      <CTASection />
    </div>
  );
}
